// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useMutation, useQuery } from '@apollo/client';
import loadable from '@loadable/component';
import type { SelectChangeEvent } from '@mui/material/Select';
import { clientLogger } from '@seedplanner/logger';
import { TASK_STATUS, isPlantStep, sendToServer, utils } from '@seedplanner/shared-utils';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import Decimal from 'decimal.js';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { ActionButtonBase, CompletedButton } from '../components/common/button';
import { TitleCard } from '../components/common/card';
import Options from '../components/common/dropdown/Options';
import { PageError } from '../components/common/error';
import {
  ALL_ITEMS,
  DATE_DROPDOWN_FILTER_FMT,
  filterTasksByName,
  getNewSelectedValues,
  makeDateDropdownFilter,
  makeMultiSelectFilter,
  makeNameFilter,
  makeSelectFilter,
} from '../components/common/filter';
import PagingView from '../components/common/view/PagingView';
import { getSoakStatus } from '../components/crop/getSoakStatus';
import OverdueCard from '../components/dashboard/OverdueCard';
import SeedNeededTable from '../components/dashboard/SeedNeededTable';
import { generateSeedNeededPdf } from '../components/dashboard/generateSeedNeededPdf';
import HarvestTable from '../components/harvest/HarvestTable';
import { groupPlantingsByTime } from '../components/harvest/groupPlantingsByTime';
import OrderTable from '../components/order/OrderTable';
import LoadingMenuItem from '../components/task/LoadingMenuItem';
import TaskGroupTable from '../components/task/TaskGroupTable';
import TaskTableOptions from '../components/task/TaskTableOptions';
import { groupTasksWithStatus } from '../components/task/groupTasks';
import { useLoggedInState } from '../context/LoggedInContext';
import { useGating, useUserContext } from '../context/UserContext';
import { useDateTransition } from '../hooks/useDateTransition';
import { Box, Grid, MenuItem } from '../lib/base';
import { PictureAsPdfIcon } from '../lib/icons';
import { DASHBOARD_QUERY, DASHBOARD_TASKS_COMPLETED_TODAY_QUERY } from '../queries/DashboardQueries';
import { EDIT_TASK_MUTATION } from '../queries/EditTaskQueries';
import * as routes from '../routes';
import type {
  ChangeTaskGroupStatusMutation,
  ChangeTaskGroupStatusMutationVariables,
  DashboardQuery,
  DashboardQueryVariables,
  DashboardTasksCompletedTodayQuery,
  DashboardTasksCompletedTodayQueryVariables,
} from '../types/__generated__/graphql';
import { getID, getNumericID, getTimeRanges } from '../utils';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);
dayjs.extend(advancedFormat);

const MarkAllCompleteButton = React.memo(
  ({ ids, onCompleted }: { readonly ids: readonly string[]; onCompleted: () => void }) => {
    const { timezone } = useUserContext();
    const { canUseBasic } = useGating();
    const { todayStart, todayEnd } = getTimeRanges(timezone);
    const [markAllComplete, { loading }] = useMutation<
      ChangeTaskGroupStatusMutation,
      ChangeTaskGroupStatusMutationVariables
    >(EDIT_TASK_MUTATION, {
      variables: { ids: ids.map((id) => getNumericID(id)) },
    });

    const handleClick = React.useCallback(() => {
      void markAllComplete({
        refetchQueries: [
          {
            query: DASHBOARD_TASKS_COMPLETED_TODAY_QUERY,
            variables: {
              todayStart: todayStart.toString(),
              todayEnd: todayEnd.toString(),
            },
          },
        ],
        onCompleted: () => {
          onCompleted();
          enqueueSnackbar('Completed tasks', { variant: 'success' });
        },
        update: (cache, { data }) => {
          const plantingsCreated = (data?.changeTaskStatuses?.plantings_created?.length ?? 0) > 0;
          const tasksCreated = (data?.changeTaskStatuses?.tasks_created?.length ?? 0) > 0;
          cache.modify({
            fields: {
              harvests(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
              plantings(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
              taskGroup(existingGroup, { DELETE }) {
                return tasksCreated ? DELETE : existingGroup;
              },
              plantingGroup(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
              planting_groups(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
              plantingTimeGroup(existingGroup, { DELETE }) {
                return plantingsCreated ? DELETE : existingGroup;
              },
            },
          });
        },
        onError: (error) => {
          enqueueSnackbar(error.message, { variant: 'error', autoHideDuration: 10000 });
        },
      });
    }, [markAllComplete, onCompleted, todayStart, todayEnd]);

    return (
      <CompletedButton
        showButton={ids.length > 0}
        disabled={loading || !canUseBasic}
        title="Mark All Complete"
        onClick={handleClick}
      />
    );
  },
);

// This prefetches when the component is mounted
const LoadableJSPDF = loadable.lib(() => import(/* webpackPrefetch: true */ 'jspdf'));

export default React.memo(function Dashboard() {
  const { timezone, permissions } = useUserContext();
  const { isLoggedIn } = useLoggedInState();
  const { todayStart, todayEnd, sevenDaysFromTodayStart, sevenDaysFromTodayEnd } = React.useMemo(
    () => getTimeRanges(timezone),
    [timezone],
  );
  const { data, loading, error, refetch } = useQuery<DashboardQuery, DashboardQueryVariables>(DASHBOARD_QUERY, {
    variables: {
      todayStart: todayStart.toString(),
      sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
    },
    skip: !isLoggedIn,
  });
  const {
    data: dataTasksCompletedToday,
    loading: loadingTasksCompletedToday,
    error: errorTasksCompletedToday,
    refetch: refetchTasksCompletedToday,
  } = useQuery<DashboardTasksCompletedTodayQuery, DashboardTasksCompletedTodayQueryVariables>(
    DASHBOARD_TASKS_COMPLETED_TODAY_QUERY,
    {
      variables: {
        todayStart: todayStart.toString(),
        todayEnd: todayEnd.toString(),
      },
      skip: !isLoggedIn,
    },
  );

  const [weekTasksFilter, setWeekTasksFilter] = React.useState([ALL_ITEMS]);
  const [weekTasksCropFilter, setWeekTasksCropFilter] = React.useState([ALL_ITEMS]);
  const [todayTasksFilter, setTodayTasksFilter] = React.useState([ALL_ITEMS]);
  const [todayTasksCropFilter, setTodayTasksCropFilter] = React.useState([ALL_ITEMS]);
  const [todayTasksStatusFilter, setTodayTasksStatusFilter] = React.useState(ALL_ITEMS);
  const [weekTasksDateFilter, setWeekTasksDateFilter] = React.useState([ALL_ITEMS]);
  const [weekTasksStatusFilter, setWeekTasksStatusFilter] = React.useState(ALL_ITEMS);
  const [nextOrdersDateFilterVal, setNextOrdersDateFilter] = React.useState([ALL_ITEMS]);
  const [nextOrdersNameFilterVal, setNextOrdersNameFilter] = React.useState([ALL_ITEMS]);
  const [seedNeededDateFilterVal, setSeedNeededDateFilter] = React.useState([ALL_ITEMS]);

  const [next7DaysTasksPage, setNext7DaysTasksPage] = React.useState(1);
  const [next7DaysOrdersPage, setNext7DaysOrdersPage] = React.useState(1);
  const [tasksCompletedTodayPage, setTasksCompletedTodayPage] = React.useState(1);
  const [overdueTasksPage, setOverdueTasksPage] = React.useState(1);

  const resetFiltersAndPages = React.useCallback(() => {
    setWeekTasksFilter([ALL_ITEMS]);
    setWeekTasksCropFilter([ALL_ITEMS]);
    setTodayTasksFilter([ALL_ITEMS]);
    setTodayTasksCropFilter([ALL_ITEMS]);
    setTodayTasksStatusFilter(ALL_ITEMS);
    setNextOrdersDateFilter([ALL_ITEMS]);
    setNextOrdersNameFilter([ALL_ITEMS]);
    setSeedNeededDateFilter([ALL_ITEMS]);
    setNext7DaysTasksPage(1);
    setNext7DaysOrdersPage(1);
    setTasksCompletedTodayPage(1);
    setOverdueTasksPage(1);
  }, []);

  const onTransition = React.useCallback(() => {
    const { todayStart, todayEnd, sevenDaysFromTodayStart } = getTimeRanges(timezone);
    refetch({
      todayStart: todayStart.toString(),
      sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
    });
    refetchTasksCompletedToday({
      todayStart: todayStart.toString(),
      todayEnd: todayEnd.toString(),
    });
    resetFiltersAndPages();
  }, [timezone, refetch, refetchTasksCompletedToday, resetFiltersAndPages]);
  useDateTransition({
    timezone,
    onTransition,
  });

  const onChangeWeekTasksCropFilter = React.useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const prevValues = [...weekTasksCropFilter];
      const selectedValues = e.target.value as string[];
      const newSelectedValues = getNewSelectedValues(prevValues, selectedValues);
      setWeekTasksCropFilter(newSelectedValues);
      setNext7DaysTasksPage(1);
    },
    [weekTasksCropFilter],
  );
  const onChangeTodayTasksCropFilter = React.useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const prevValues = [...todayTasksCropFilter];
      const selectedValues = e.target.value as string[];
      const newSelectedValues = getNewSelectedValues(prevValues, selectedValues);
      setTodayTasksCropFilter(newSelectedValues);
    },
    [todayTasksCropFilter],
  );
  const onChangeTodayTasksStatusFilter = React.useCallback((e: SelectChangeEvent<string>) => {
    setTodayTasksStatusFilter(e.target.value);
  }, []);
  const onChangeWeekTasksFilter = React.useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const prevValues = [...weekTasksFilter];
      const selectedValues = e.target.value as string[];
      const newSelectedValues = getNewSelectedValues(prevValues, selectedValues);
      setWeekTasksFilter(newSelectedValues);
      setNext7DaysTasksPage(1);
    },
    [weekTasksFilter],
  );
  const onChangeWeekTasksStatusFilter = React.useCallback((e: SelectChangeEvent<string>) => {
    setWeekTasksStatusFilter(e.target.value);
  }, []);
  const onChangeTodayTasksFilter = React.useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const prevValues = [...todayTasksFilter];
      const selectedValues = e.target.value as string[];
      const newSelectedValues = getNewSelectedValues(prevValues, selectedValues);
      setTodayTasksFilter(newSelectedValues);
    },
    [todayTasksFilter],
  );
  const onChangeWeekTasksDateFilter = React.useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const prevValues = [...weekTasksDateFilter];
      const selectedValues = e.target.value as string[];
      const newSelectedValues = getNewSelectedValues(prevValues, selectedValues);
      setWeekTasksDateFilter(newSelectedValues);
      setNext7DaysTasksPage(1);
    },
    [weekTasksDateFilter],
  );
  const onChangeSeedNeededDateFilter = React.useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const prevValues = [...seedNeededDateFilterVal];
      const selectedValues = e.target.value as string[];
      const newSelectedValues = getNewSelectedValues(prevValues, selectedValues);
      setSeedNeededDateFilter(newSelectedValues);
    },
    [seedNeededDateFilterVal],
  );
  const onChangeWeekOrdersDateFilter = React.useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const prevValues = [...nextOrdersDateFilterVal];
      const selectedValues = e.target.value as string[];
      const newSelectedValues = getNewSelectedValues(prevValues, selectedValues);
      setNextOrdersDateFilter(newSelectedValues);
      setNext7DaysOrdersPage(1);
    },
    [nextOrdersDateFilterVal],
  );
  const onChangeWeekOrdersNameFilter = React.useCallback(
    (e: SelectChangeEvent<string[]>) => {
      const prevValues = [...nextOrdersNameFilterVal];
      const selectedValues = e.target.value as string[];
      const newSelectedValues = getNewSelectedValues(prevValues, selectedValues);
      setNextOrdersNameFilter(newSelectedValues);
      setNext7DaysOrdersPage(1);
    },
    [nextOrdersNameFilterVal],
  );

  const userCanCompleteTasks = permissions.can_complete_tasks;

  const tasksData = React.useMemo(() => data?.tasks.edges.map(({ node }) => node) ?? [], [data]);
  const tasksCompletedTodayData = React.useMemo(
    () => dataTasksCompletedToday?.completedTasks.edges.map(({ node }) => node) ?? [],
    [dataTasksCompletedToday],
  );
  const todayTasksData = React.useMemo(
    () => tasksData.filter((task) => task.task_time >= todayStart && task.task_time <= todayEnd),
    [tasksData, todayStart, todayEnd],
  );
  const weekTasksData = React.useMemo(
    () => tasksData.filter((task) => task.task_time >= todayEnd && task.task_time <= sevenDaysFromTodayEnd),
    [tasksData, todayEnd, sevenDaysFromTodayEnd],
  );
  const todayAndWeekTasksDataOnlyPlant = React.useMemo(
    () => [...todayTasksData, ...weekTasksData].filter((task) => isPlantStep(task.task_name)),
    [todayTasksData, weekTasksData],
  );
  const traysNeededPerDayPerCrop = React.useMemo(
    () => groupTasksWithStatus(todayAndWeekTasksDataOnlyPlant),
    [todayAndWeekTasksDataOnlyPlant],
  );
  // Go through each group of trays per day-crop. Get a single task for the task time
  // Then create new map from task time to trays. Use Math.ceil before adding trays to map

  interface SeedNeedPerDayPerCrop {
    readonly crop: {
      readonly id: string;
      readonly crop_name: string;
      readonly sowing_density: string;
      readonly sow_density_unit: string;
      readonly soakStatus: string;
    };
    readonly time: number;
    readonly totalTrays: number;
    readonly totalSeed: number;
  }
  const seedNeedPerDayPerCrop = React.useMemo(() => {
    return [...traysNeededPerDayPerCrop.values()].reduce(
      (acc, { tasks, traysNotRounded }) => {
        const oneTask = tasks[0];
        if (oneTask.crop == null) {
          return acc;
        }
        if (oneTask.crop.sowing_density == null) {
          return acc;
        }
        if (oneTask.crop.sow_density_unit == null) {
          return acc;
        }
        const cropSteps = oneTask.crop.steps.edges.map(({ node }) => node);
        const soakStatus = getSoakStatus(cropSteps);

        let time = oneTask.task_time;
        const day = dayjs.unix(time).tz(timezone);
        // If for any reason the task time is not 12pm, set it to 12pm
        // Primarily for DST where hour might be 1pm
        if (day.hour() !== 12) {
          const newDay = day.hour(12);
          time = newDay.unix();
        }
        const trays = Math.ceil(traysNotRounded);
        const seedNeeded = Decimal.mul(oneTask.crop.sowing_density, trays).toNumber();

        const key = `${oneTask.crop.id}-${time}`;
        const maybeExisting = acc.get(key);
        if (maybeExisting === undefined) {
          acc.set(key, {
            totalTrays: trays,
            totalSeed: seedNeeded,
            crop: {
              id: oneTask.crop.id,
              crop_name: oneTask.crop.crop_name,
              sowing_density: oneTask.crop.sowing_density,
              sow_density_unit: oneTask.crop.sow_density_unit,
              soakStatus,
            },
            time,
          });
        } else {
          acc.set(key, {
            totalTrays: Decimal.add(maybeExisting.totalTrays, trays).toNumber(),
            totalSeed: Decimal.add(maybeExisting.totalSeed, seedNeeded).toNumber(),
            crop: {
              id: oneTask.crop.id,
              crop_name: oneTask.crop.crop_name,
              sowing_density: oneTask.crop.sowing_density,
              sow_density_unit: oneTask.crop.sow_density_unit,
              soakStatus,
            },
            time,
          });
        }

        return acc;
      },
      new Map<
        string, // Crop + Time Key
        // Total trays, total seed needed and sow density unit
        SeedNeedPerDayPerCrop
      >(),
    );
  }, [traysNeededPerDayPerCrop, timezone]);

  const seedNeededValues: readonly SeedNeedPerDayPerCrop[] = React.useMemo(() => {
    return [...seedNeedPerDayPerCrop.values()];
  }, [seedNeedPerDayPerCrop]);
  const seedNeededDateFilter = React.useMemo(
    () =>
      makeDateDropdownFilter({
        objects: seedNeededValues,
        value: seedNeededDateFilterVal,
        onChangeValue: onChangeSeedNeededDateFilter,
        getTime: (val: SeedNeedPerDayPerCrop) => val.time,
        userTimezone: timezone,
      }),
    [seedNeededDateFilterVal, onChangeSeedNeededDateFilter, seedNeededValues, timezone],
  );

  const filteredSeedNeededValues = React.useMemo(() => {
    if (seedNeededDateFilterVal.includes(ALL_ITEMS)) {
      return seedNeededValues;
    }
    return seedNeededValues.filter((val) =>
      seedNeededDateFilterVal.includes(dayjs.unix(val.time).tz(timezone).format(DATE_DROPDOWN_FILTER_FMT)),
    );
  }, [seedNeededDateFilterVal, seedNeededValues, timezone]);
  const [seedNeededOptionsClose, setSeedNeededOptionsClose] = React.useState(false);
  const seedNeededOptions = React.useMemo(() => {
    const exportPdfItem = (
      <LoadableJSPDF key="export-to-pdf-loadable" fallback={<LoadingMenuItem />}>
        {({ jsPDF }) => (
          <MenuItem
            key="export-to-pdf-menu-item"
            onClick={() => {
              try {
                const title = `Next 7 Days' Trays & Seed Needed - ${todayDateLabel} to ${sevenDaysFromTodayEndFmt}`;
                generateSeedNeededPdf(jsPDF, {
                  datesAndTrays: filteredSeedNeededValues,
                  downloadTitle: title,
                  title: title,
                  timezone,
                });
              } catch (e) {
                clientLogger.error('Error generating PDF', { error: e });
                sendToServer({
                  route: routes.ERROR,
                  body: JSON.stringify({
                    errorMessage: 'Error generating PDF with JSPDF in dashboard seed needed options',
                    location: 'dashboard',
                  }),
                  title: 'no_jspdf_on_click_seed_needed_table',
                });
                enqueueSnackbar('Something went wrong generating the PDF! The team has been notified.', {
                  variant: 'error',
                  autoHideDuration: 10000,
                });
              }
              setSeedNeededOptionsClose(true);
            }}
            disableRipple
          >
            <PictureAsPdfIcon />
            Export to PDF
          </MenuItem>
        )}
      </LoadableJSPDF>
    );

    const options = [exportPdfItem];

    return <Options close={seedNeededOptionsClose} options={options} />;
  }, [seedNeededOptionsClose, timezone, filteredSeedNeededValues]);
  const seedNeededCard = React.useMemo(() => {
    return (
      <TitleCard
        title="Next 7 Days' Trays/Seed Needed For Planting"
        rightElement={seedNeededOptions}
        extraHeaderContent={<Box>{seedNeededDateFilter}</Box>}
        loading={loading}
        loadingError={data == null || data.tasks == null}
        content={<SeedNeededTable datesAndTrays={filteredSeedNeededValues} />}
      />
    );
  }, [loading, data, seedNeededDateFilter, filteredSeedNeededValues, seedNeededOptions]);

  const todaySelectFilter = React.useMemo(
    () =>
      makeNameFilter({
        isForTasks: true,
        objects: todayTasksData,
        getName: (t) => t.task_name,
        value: todayTasksFilter,
        onChangeValue: onChangeTodayTasksFilter,
      }),
    [todayTasksData, todayTasksFilter, onChangeTodayTasksFilter],
  );
  const todayTasksStatusFilterMenu = React.useMemo(
    () =>
      makeSelectFilter({
        value: todayTasksStatusFilter,
        onChangeValue: onChangeTodayTasksStatusFilter,
        label: 'Status Filter',
        id: 'today-tasks-status-filter',
        options: [
          { value: TASK_STATUS.PLANNED, label: 'Planned' },
          { value: TASK_STATUS.COMPLETED, label: 'Completed' },
        ],
      }),
    [todayTasksStatusFilter, onChangeTodayTasksStatusFilter],
  );
  const todayTasksCropFilterMenu = React.useMemo(() => {
    const crops = todayTasksData.map((t) => t.crop).filter(utils.notNull);
    const seen = new Set<string>();
    const cropsToUse = crops.filter((c) => {
      const id = c.id;
      if (seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    });
    const options = cropsToUse.map((c) => ({ value: getID(c.id), label: c.crop_name }));
    options.sort((a, b) => a.label.localeCompare(b.label));

    return makeMultiSelectFilter({
      value: todayTasksCropFilter,
      onChangeValue: onChangeTodayTasksCropFilter,
      label: 'Crop Filter',
      id: 'today-tasks-crop-filter',
      options,
    });
  }, [todayTasksCropFilter, onChangeTodayTasksCropFilter, todayTasksData]);
  const todayTasksFiltersRow = React.useMemo(
    () => (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row', gap: 1 }}>
        <Box>{todaySelectFilter}</Box>
        <Box>{todayTasksCropFilterMenu}</Box>
        <Box>{todayTasksStatusFilterMenu}</Box>
      </Box>
    ),
    [todaySelectFilter, todayTasksCropFilterMenu, todayTasksStatusFilterMenu],
  );

  const dateFormat = 'ddd, MMM Do';
  const todayDateLabel = React.useMemo(
    () => dayjs.unix(todayStart).tz(timezone).format(dateFormat),
    [todayStart, timezone],
  );

  const tasksToday = React.useMemo(
    () => (
      <TitleCard
        title={`Today's Tasks - ${todayDateLabel}`}
        extraHeaderContent={todayTasksFiltersRow}
        rightElement={
          <TaskTableOptions
            tasks={filterTasksByName({
              tasks: todayTasksData,
              filterValue: todayTasksFilter,
              getTaskName: (t) => t.task_name,
            })
              .filter((task) => {
                if (todayTasksStatusFilter === ALL_ITEMS) {
                  return true;
                }

                return todayTasksStatusFilter === TASK_STATUS.COMPLETED
                  ? task.task_status === TASK_STATUS.COMPLETED
                  : task.task_status !== TASK_STATUS.COMPLETED;
              })
              .filter((task) => {
                if (todayTasksCropFilter.includes(ALL_ITEMS)) {
                  return true;
                }
                if (task.crop == null) {
                  return false;
                }
                const taskCropId = getID(task.crop.id);

                return todayTasksCropFilter.includes(taskCropId);
              })}
            onSuccess={() => refetch()}
            showComplete
            downloadTitle={`Today's Tasks- ${todayDateLabel}`}
          />
        }
        loading={loading}
        loadingError={data == null || data.tasks == null}
        content={
          <TaskGroupTable
            showActionButtons={true}
            showPlantingTimeColumn={true}
            showHarvestTimeColumn={true}
            taskGroups={filterTasksByName({
              tasks: [...groupTasksWithStatus(todayTasksData).values()],
              filterValue: todayTasksFilter,
              getTaskName: (group) => group.tasks[0].task_name,
            })
              .filter((group) => {
                if (todayTasksStatusFilter === ALL_ITEMS) {
                  return true;
                }

                return todayTasksStatusFilter === TASK_STATUS.COMPLETED ? group.isCompleted : !group.isCompleted;
              })
              .filter((group) => {
                if (todayTasksCropFilter.includes(ALL_ITEMS)) {
                  return true;
                }
                if (group.tasks[0].crop == null) {
                  return false;
                }
                const taskCropId = getID(group.tasks[0].crop.id);

                return todayTasksCropFilter.includes(taskCropId);
              })
              .map(({ tasks }) => tasks)}
          />
        }
      />
    ),
    [
      todayTasksFiltersRow,
      todayTasksData,
      todayTasksCropFilter,
      todayTasksFilter,
      refetch,
      loading,
      data,
      todayDateLabel,
      todayTasksStatusFilter,
    ],
  );

  const tasksCompletedToday = React.useMemo(() => {
    const pageSize = 20;
    const allTaskGroups = [...groupTasksWithStatus(tasksCompletedTodayData).values()]
      .filter(({ isCompleted }) => isCompleted)
      .map(({ tasks }) => tasks);
    const totalItems = allTaskGroups.length;
    const hasPreviousPage = tasksCompletedTodayPage > 1;
    const hasNextPage = totalItems > tasksCompletedTodayPage * pageSize;
    const tasksGroups = allTaskGroups.slice(
      tasksCompletedTodayPage * pageSize - pageSize,
      tasksCompletedTodayPage * pageSize,
    );

    return (
      <TitleCard
        title={`Tasks Completed Today - ${todayDateLabel}`}
        rightElement={
          <ActionButtonBase
            title="Refresh"
            showButton={true}
            disabled={loading || loadingTasksCompletedToday}
            onClick={() => {
              refetchTasksCompletedToday();
            }}
          />
        }
        loading={loading || loadingTasksCompletedToday}
        loadingError={
          data == null ||
          data.tasks == null ||
          dataTasksCompletedToday == null ||
          dataTasksCompletedToday.completedTasks == null
        }
        content={
          <PagingView
            content={
              <TaskGroupTable showTaskCompletedByColumn={true} showActionButtons={false} taskGroups={tasksGroups} />
            }
            isInitialLoad={loading}
            isLoadingMore={false}
            page={tasksCompletedTodayPage}
            pageSize={pageSize}
            currentPageSize={tasksGroups.length}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            onUpdatePage={(pageNum: number) => {
              setTasksCompletedTodayPage(pageNum);
            }}
            // error={error}
            omitPager={!hasNextPage && !hasPreviousPage}
            disablePadding={false}
          />
        }
      />
    );
  }, [
    tasksCompletedTodayData,
    loading,
    loadingTasksCompletedToday,
    data,
    refetchTasksCompletedToday,
    dataTasksCompletedToday,
    todayDateLabel,
    tasksCompletedTodayPage,
  ]);

  const thisWeekSelectFilter = React.useMemo(
    () =>
      makeNameFilter({
        isForTasks: true,
        objects: weekTasksData.filter((t) => t.task_status !== TASK_STATUS.COMPLETED),
        value: weekTasksFilter,
        getName: (t) => t.task_name,
        onChangeValue: onChangeWeekTasksFilter,
      }),
    [weekTasksData, weekTasksFilter, onChangeWeekTasksFilter],
  );
  const thisWeekTasksStatusFilterMenu = React.useMemo(
    () =>
      makeSelectFilter({
        value: weekTasksStatusFilter,
        onChangeValue: onChangeWeekTasksStatusFilter,
        label: 'Status Filter',
        id: 'this-week-tasks-status-filter',
        options: [
          { value: TASK_STATUS.PLANNED, label: 'Planned' },
          { value: TASK_STATUS.COMPLETED, label: 'Completed' },
        ],
      }),
    [weekTasksStatusFilter, onChangeWeekTasksStatusFilter],
  );
  const thisWeekTasksCropFilterMenu = React.useMemo(() => {
    const crops = weekTasksData.map((t) => t.crop).filter(utils.notNull);
    const seen = new Set<string>();
    const cropsToUse = crops.filter((c) => {
      const id = c.id;
      if (seen.has(id)) {
        return false;
      }
      seen.add(id);
      return true;
    });
    const options = cropsToUse.map((c) => ({ value: getID(c.id), label: c.crop_name }));
    options.sort((a, b) => a.label.localeCompare(b.label));

    return makeMultiSelectFilter({
      value: weekTasksCropFilter,
      onChangeValue: onChangeWeekTasksCropFilter,
      label: 'Crop Filter',
      id: 'this-week-tasks-crop-filter',
      options,
    });
  }, [weekTasksCropFilter, onChangeWeekTasksCropFilter, weekTasksData]);
  const thisWeekDateFilter = React.useMemo(
    () =>
      makeDateDropdownFilter({
        objects: weekTasksData.filter((t) => t.task_status !== TASK_STATUS.COMPLETED),
        value: weekTasksDateFilter,
        getTime: (t) => t.task_time,
        onChangeValue: onChangeWeekTasksDateFilter,
        userTimezone: timezone,
      }),
    [weekTasksData, weekTasksDateFilter, onChangeWeekTasksDateFilter, timezone],
  );

  const weekTasksFilters = React.useMemo(
    () => (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row', gap: 1 }}>
        <Box>{thisWeekSelectFilter}</Box>
        <Box>{thisWeekDateFilter}</Box>
        <Box>{thisWeekTasksCropFilterMenu}</Box>
        <Box>{thisWeekTasksStatusFilterMenu}</Box>
      </Box>
    ),
    [thisWeekSelectFilter, thisWeekDateFilter, thisWeekTasksCropFilterMenu, thisWeekTasksStatusFilterMenu],
  );

  const sevenDaysFromTodayEndFmt = React.useMemo(
    () => dayjs.unix(sevenDaysFromTodayStart).tz(timezone).format(dateFormat),
    [sevenDaysFromTodayStart, timezone],
  );

  const thisWeekTaskOptions = React.useMemo(
    () => (
      <TaskTableOptions
        tasks={filterTasksByName({
          tasks: weekTasksData,
          filterValue: weekTasksFilter,
          getTaskName: (task) => task.task_name,
        })
          .filter((task) => {
            if (weekTasksStatusFilter === ALL_ITEMS) {
              return true;
            }

            return weekTasksStatusFilter === TASK_STATUS.COMPLETED
              ? task.task_status === TASK_STATUS.COMPLETED
              : task.task_status !== TASK_STATUS.COMPLETED;
          })
          .filter((task) => {
            if (weekTasksCropFilter.includes(ALL_ITEMS)) {
              return true;
            }
            if (task.crop == null) {
              return false;
            }
            const taskCropId = getID(task.crop.id);

            return weekTasksCropFilter.includes(taskCropId);
          })
          .filter((task) =>
            weekTasksDateFilter.includes(ALL_ITEMS)
              ? true
              : weekTasksDateFilter.includes(dayjs.unix(task.task_time).tz(timezone).format(DATE_DROPDOWN_FILTER_FMT)),
          )}
        downloadTitle={`Next 7 Days' Tasks- ${todayDateLabel} to ${sevenDaysFromTodayEndFmt}`}
      />
    ),
    [
      weekTasksData,
      weekTasksFilter,
      weekTasksCropFilter,
      weekTasksDateFilter,
      timezone,
      todayDateLabel,
      sevenDaysFromTodayEndFmt,
      weekTasksStatusFilter,
    ],
  );

  const tasksThisWeek = React.useMemo(() => {
    const filteredTaskGroups = filterTasksByName({
      tasks: [...groupTasksWithStatus(weekTasksData).values()],
      filterValue: weekTasksFilter,
      getTaskName: (group) => group.tasks[0].task_name,
    })
      .filter((group) => {
        if (weekTasksStatusFilter === ALL_ITEMS) {
          return true;
        }

        return weekTasksStatusFilter === TASK_STATUS.COMPLETED ? group.isCompleted : !group.isCompleted;
      })
      .filter((group) => {
        if (weekTasksCropFilter.includes(ALL_ITEMS)) {
          return true;
        }
        if (group.tasks[0].crop == null) {
          return false;
        }
        const taskCropId = getID(group.tasks[0].crop.id);

        return weekTasksCropFilter.includes(taskCropId);
      })
      .filter(({ tasks }) =>
        weekTasksDateFilter.includes(ALL_ITEMS)
          ? true
          : weekTasksDateFilter.includes(dayjs.unix(tasks[0].task_time).tz(timezone).format(DATE_DROPDOWN_FILTER_FMT)),
      );
    const pageSize = 20;
    const totalItems = filteredTaskGroups.length;
    const hasPreviousPage = next7DaysTasksPage > 1;
    const hasNextPage = totalItems > next7DaysTasksPage * pageSize;
    const taskGroups = filteredTaskGroups.map(({ tasks }) => tasks);
    taskGroups.sort((a, b) => a[0].task_time - b[0].task_time);
    const tasks = taskGroups.slice(next7DaysTasksPage * pageSize - pageSize, next7DaysTasksPage * pageSize);
    const currentPageSize = tasks.length;

    return (
      <TitleCard
        title="Next 7 Days' Tasks"
        extraHeaderContent={weekTasksFilters}
        rightElement={thisWeekTaskOptions}
        loading={loading}
        loadingError={data == null || data.tasks == null}
        content={
          <PagingView
            content={
              <TaskGroupTable
                showActionButtons={true}
                showPlantingTimeColumn={true}
                showHarvestTimeColumn={true}
                taskGroups={tasks}
              />
            }
            isInitialLoad={loading}
            isLoadingMore={false}
            page={next7DaysTasksPage}
            pageSize={pageSize}
            currentPageSize={currentPageSize}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            onUpdatePage={(pageNum: number) => {
              setNext7DaysTasksPage(pageNum);
            }}
            // error={error}
            omitPager={!hasNextPage && !hasPreviousPage}
            disablePadding={false}
          />
        }
      />
    );
  }, [
    weekTasksFilters,
    thisWeekTaskOptions,
    loading,
    data,
    weekTasksData,
    weekTasksFilter,
    weekTasksDateFilter,
    weekTasksCropFilter,
    timezone,
    next7DaysTasksPage,
    weekTasksStatusFilter,
  ]);

  const overdueTasksData = React.useMemo(() => data?.overdueTasks.edges.map(({ node }) => node) ?? [], [data]);
  const groupedOverdueTasks = React.useMemo(() => {
    return Array.from(groupTasksWithStatus(overdueTasksData).values()).filter(({ isCompleted }) => !isCompleted);
  }, [overdueTasksData]);
  const overdueTasks = React.useMemo(() => {
    const pageSize = 20;
    const totalItems = groupedOverdueTasks.length;
    const hasPreviousPage = overdueTasksPage > 1;
    const hasNextPage = totalItems > overdueTasksPage * pageSize;
    const allTaskGroups = groupedOverdueTasks.map(({ tasks }) => tasks);
    allTaskGroups.sort((a, b) => a[0].task_time - b[0].task_time);
    const tasksGroups = allTaskGroups.slice(overdueTasksPage * pageSize - pageSize, overdueTasksPage * pageSize);

    return (
      <TitleCard
        id="overdue-tasks-card"
        title="Overdue Tasks"
        rightElement={
          userCanCompleteTasks ? (
            <MarkAllCompleteButton
              onCompleted={() => {
                void refetch();
                void refetchTasksCompletedToday();
              }}
              ids={overdueTasksData.filter((t) => t.task_status !== TASK_STATUS.COMPLETED).map((t) => t.id)}
            />
          ) : (
            <></>
          )
        }
        loading={loading}
        loadingError={data == null || data.tasks == null}
        content={
          <PagingView
            content={
              <TaskGroupTable
                showActionButtons={true}
                showPlantingTimeColumn={true}
                showHarvestTimeColumn={true}
                taskGroups={tasksGroups}
              />
            }
            isInitialLoad={loading}
            isLoadingMore={false}
            page={overdueTasksPage}
            pageSize={pageSize}
            currentPageSize={tasksGroups.length}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            onUpdatePage={(pageNum: number) => {
              setOverdueTasksPage(pageNum);
            }}
            // error={error}
            omitPager={!hasNextPage && !hasPreviousPage}
            disablePadding={false}
          />
        }
      />
    );
  }, [
    userCanCompleteTasks,
    refetch,
    refetchTasksCompletedToday,
    overdueTasksData,
    groupedOverdueTasks,
    loading,
    data,
    overdueTasksPage,
  ]);

  const overdueTasksCountCard = React.useMemo(
    () => <OverdueCard title="Overdue Tasks" count={groupedOverdueTasks.length} anchor="overdue-tasks-card" />,
    [groupedOverdueTasks],
  );

  const plantingsData = React.useMemo(() => data?.plantings.edges.map(({ node }) => node) ?? [], [data]);
  // Harvests where planting_time >= todayStart and planting_time <= sevenDaysFromTodayStart
  const thisWeekPlantingsData = React.useMemo(
    () =>
      plantingsData.filter(
        (planting) => planting.planting_time >= todayStart && planting.planting_time <= sevenDaysFromTodayStart,
      ),
    [plantingsData, todayStart, sevenDaysFromTodayStart],
  );

  const weekPlantingsGrouped = React.useMemo(() => {
    const grouped = Array.from(groupPlantingsByTime(thisWeekPlantingsData).values());
    grouped.sort((a, b) => a.time - b.time);
    return grouped;
  }, [thisWeekPlantingsData]);

  const harvestsThisWeek = React.useMemo(
    () => (
      <TitleCard
        title="Next 7 Days' Harvests"
        rightElement={<div />}
        loading={loading}
        loadingError={data == null || data.plantings == null}
        content={<HarvestTable harvestGroups={weekPlantingsGrouped.map((g) => g.plantings)} />}
      />
    ),
    [loading, data, weekPlantingsGrouped],
  );

  const allOrders = React.useMemo(() => data?.orders.edges.map(({ node }) => node) ?? [], [data]);
  const nextWeekOrders = React.useMemo(
    () => allOrders.filter((order) => order.order_fulfillment_time > todayEnd),
    [allOrders, todayEnd],
  );

  const nextOrdersDateFilter = React.useMemo(
    () =>
      makeDateDropdownFilter({
        objects: nextWeekOrders,
        value: nextOrdersDateFilterVal,
        getTime: (o) => o.order_fulfillment_time,
        onChangeValue: onChangeWeekOrdersDateFilter,
        userTimezone: timezone,
      }),
    [nextWeekOrders, nextOrdersDateFilterVal, timezone, onChangeWeekOrdersDateFilter],
  );

  const nextOrdersNameFilter = React.useMemo(
    () =>
      makeNameFilter({
        objects: nextWeekOrders,
        value: nextOrdersNameFilterVal,
        getName: (o) => o.customer,
        onChangeValue: onChangeWeekOrdersNameFilter,
        label: 'Customer Filter',
      }),
    [nextWeekOrders, nextOrdersNameFilterVal, onChangeWeekOrdersNameFilter],
  );

  const nextOrdersFiltersRow = React.useMemo(
    () => (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row', gap: 1 }}>
        <Box>{nextOrdersNameFilter}</Box>
        <Box>{nextOrdersDateFilter}</Box>
      </Box>
    ),
    [nextOrdersDateFilter, nextOrdersNameFilter],
  );

  const filteredNextWeekOrders = React.useMemo(() => {
    return nextWeekOrders.filter((order) => {
      const orderTime = dayjs.unix(order.order_fulfillment_time).tz(timezone);
      const orderTimeFmt = orderTime.format(DATE_DROPDOWN_FILTER_FMT);
      return (
        (nextOrdersDateFilterVal.includes(ALL_ITEMS) ? true : nextOrdersDateFilterVal.includes(orderTimeFmt)) &&
        (nextOrdersNameFilterVal.includes(ALL_ITEMS) ? true : nextOrdersNameFilterVal.includes(order.customer))
      );
    });
  }, [nextWeekOrders, nextOrdersDateFilterVal, nextOrdersNameFilterVal, timezone]);

  const ordersThisWeek = React.useMemo(() => {
    const pageSize = 20;
    const totalItems = filteredNextWeekOrders.length;
    const hasPreviousPage = next7DaysOrdersPage > 1;
    const hasNextPage = totalItems > next7DaysOrdersPage * pageSize;
    const orders = filteredNextWeekOrders.slice(
      next7DaysOrdersPage * pageSize - pageSize,
      next7DaysOrdersPage * pageSize,
    );
    const currentPageSize = orders.length;

    return (
      <TitleCard
        title="Next 7 Days' Orders"
        rightElement={<div />}
        loading={loading}
        loadingError={data == null || data.orders == null}
        content={
          <PagingView
            content={<OrderTable orders={orders} />}
            isInitialLoad={loading}
            isLoadingMore={false}
            page={next7DaysOrdersPage}
            pageSize={pageSize}
            currentPageSize={currentPageSize}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            onUpdatePage={(pageNum: number) => {
              setNext7DaysOrdersPage(pageNum);
            }}
            // error={error}
            omitPager={!hasNextPage && !hasPreviousPage}
            disablePadding={false}
          />
        }
        extraHeaderContent={nextOrdersFiltersRow}
      />
    );
  }, [loading, data, filteredNextWeekOrders, nextOrdersFiltersRow, next7DaysOrdersPage]);

  if (error) {
    return <PageError error={error} />;
  }
  if (errorTasksCompletedToday) {
    return <PageError error={errorTasksCompletedToday} />;
  }

  return (
    <>
      <Helmet>
        <title>{'Dashboard'}</title>
      </Helmet>
      <Grid container display="flex" justifyContent="center" flexDirection="column" gap={{ xs: 1, sm: 2 }}>
        {tasksToday}
        {overdueTasksCountCard}
        {tasksThisWeek}
        {seedNeededCard}
        {harvestsThisWeek}
        {ordersThisWeek}
        {tasksCompletedToday}
        {overdueTasks}
      </Grid>
    </>
  );
});
